import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { axios } from '../../libs/axios';
import logger from '../../utils/logger';
import { ACTIVITY_ICONS } from './leadActivityIcons';
import { LeadActivityComposer } from './LeadActivityComposer';
import { formatActivityBody } from './formatActivityBody';
import { formatFromNow } from '../../utils/relativeTime';

function getInitials(name, email) {
  const source = (name || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function formatDateDMY(date) {
  if (!date) return '';
  return moment(date).format('DD.MM.YYYY');
}

export const LeadDetailModal = ({
  entryId,
  listId,
  visible,
  onClose,
  onUpdated,
  t,
  locale = 'en',
  LeadStatusBadge,
}) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activityType, setActivityType] = useState('note');
  const [activityBody, setActivityBody] = useState('');
  const [savingActivity, setSavingActivity] = useState(false);
  const [nextActionDate, setNextActionDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);

  const fetchEntry = useCallback(async () => {
    if (!entryId) return;
    setLoading(true);
    try {
      const response = await axios.get(`/leads/entries/${entryId}`, {
        params: listId ? { listId } : undefined,
      });
      const data = response?.entry;
      setEntry(data || null);
      if (data?.nextActionAt) {
        setNextActionDate(moment(data.nextActionAt).format('YYYY-MM-DD'));
      } else {
        setNextActionDate('');
      }
    } catch (error) {
      logger.error(error);
      window.nError({ text: error?.message || t('leads.drawerLoadError') });
    } finally {
      setLoading(false);
    }
  }, [entryId, listId, t]);

  useEffect(() => {
    if (visible && entryId) {
      fetchEntry();
    } else {
      setEntry(null);
      setActivityBody('');
      setActivityType('note');
    }
  }, [visible, entryId, fetchEntry]);

  const handleStatusChange = async (id, payload) => {
    const response = await axios.patch(`/leads/entries/${id}/status`, payload);
    const updated = response?.entry;
    if (updated) {
      setEntry((prev) => ({ ...prev, ...updated }));
      onUpdated?.(updated);
      if (updated.nextActionAt) {
        setNextActionDate(moment(updated.nextActionAt).format('YYYY-MM-DD'));
        window.nSuccess({
          text: t('leads.followUpSet', { date: formatDateDMY(updated.nextActionAt) }),
        });
      }
    }
    return updated;
  };

  const handleNextActionSave = async () => {
    if (!entry) return;
    setSavingDate(true);
    try {
      const nextActionAt = nextActionDate
        ? moment(nextActionDate).endOf('day').toISOString()
        : null;
      const response = await axios.patch(`/leads/entries/${entry._id}`, { nextActionAt });
      const updated = response?.entry;
      if (updated) {
        await fetchEntry();
        onUpdated?.(updated);
      }
    } catch (error) {
      window.nError({ text: error?.message || t('leads.dateUpdateError') });
    } finally {
      setSavingDate(false);
    }
  };

  const handleAddActivity = async (type, body) => {
    if (!entry || savingActivity) return;
    setSavingActivity(true);
    try {
      const response = await axios.post(`/leads/entries/${entry._id}/activities`, {
        type,
        body,
      });
      const updated = response?.entry;
      if (updated) {
        await fetchEntry();
        onUpdated?.(updated);
        setActivityBody('');
      }
    } catch (error) {
      window.nError({ text: error?.message || t('leads.activityAddError') });
    } finally {
      setSavingActivity(false);
    }
  };

  if (!visible) return null;

  const activities = [...(entry?.activities || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  return (
    <div className="modal prepayment leads-detail-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modalclose" onClick={onClose}>&times;</span>
          {entry ? (
            <div className="leads-modal-client-header">
              <div className="leads-modal-avatar">
                {getInitials(entry.clientName, entry.clientEmail)}
              </div>
              <div>
                <h2>{entry.clientName || t('common.emptyValue')}</h2>
                <p className="leads-modal-contact">
                  {[entry.clientEmail, entry.clientPhone].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
          ) : (
            <h2>{t('leads.title')}</h2>
          )}
        </div>
        <div className="modal-form leads-modal-body">
          {loading && !entry ? (
            <p>{t('common.loading')}</p>
          ) : entry ? (
            <>
              <div className="leads-modal-controls">
                <div className="leads-modal-control-row">
                  <label>{t('leads.colStatus')}</label>
                  <LeadStatusBadge
                    entry={entry}
                    t={t}
                    onStatusChange={handleStatusChange}
                  />
                </div>
                <div className="leads-modal-control-row">
                  <label>{t('leads.colNextAction')}</label>
                  <div className="leads-modal-date-row">
                    <input
                      type="date"
                      value={nextActionDate}
                      onChange={(e) => setNextActionDate(e.target.value)}
                    />
                    <button
                      type="button"
                      className="button button-small"
                      disabled={savingDate}
                      onClick={handleNextActionSave}
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </div>
              </div>

              <LeadActivityComposer
                activityType={activityType}
                activityBody={activityBody}
                onTypeChange={setActivityType}
                onBodyChange={setActivityBody}
                onSubmit={handleAddActivity}
                saving={savingActivity}
                t={t}
              />

              <div className="leads-modal-timeline">
                <h4>{t('leads.timeline')}</h4>
                {activities.length === 0 ? (
                  <p className="leads-timeline-empty">{t('leads.timelineEmpty')}</p>
                ) : (
                  <ul>
                    {activities.map((activity) => {
                      const activityBody = formatActivityBody(activity, t);
                      return (
                      <li
                        key={activity._id || `${activity.type}-${activity.createdAt}`}
                        className={`leads-timeline-item leads-timeline-${activity.type}`}
                      >
                        <i className={ACTIVITY_ICONS[activity.type] || ACTIVITY_ICONS.note} />
                        <div className="leads-timeline-body">
                          <div className="leads-timeline-meta">
                            <span>{t(`leads.activityType.${activity.type}`)}</span>
                            {activity.author && (
                              <span> · {activity.author.fullName || activity.author.email}</span>
                            )}
                            <span> · {formatFromNow(activity.createdAt, locale)}</span>
                          </div>
                          {activityBody && <p>{activityBody}</p>}
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <p>{t('leads.drawerLoadError')}</p>
          )}
        </div>
      </div>
    </div>
  );
};
